import React, { useMemo, useState } from 'react';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import { BaseNode } from './BaseNode';
import { NodeActionsDropdown } from './NodeActionsDropdown';
import EditModal from '@/components/images/EditModal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWorkflowStore } from '@/lib/workflows/store';

interface OutputNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
}

// Helper function to download image
const downloadImage = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
  }
};

export function OutputNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
}: OutputNodeProps) {
  const { t } = useLocale('common');
  const isMobile = useIsMobile();
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const [showEditModal, setShowEditModal] = useState(false);
  // Extract media info for dropdown
  const { mediaUrl, mediaType } = useMemo(() => {
    if (typeof data.result === 'string') {
      const resultUrl = data.result;
      if (
        resultUrl.match(/\.(jpeg|jpg|gif|png|webp)$/i) ||
        resultUrl.includes('image')
      ) {
        return { mediaUrl: resultUrl, mediaType: 'image' as const };
      }
      if (
        resultUrl.match(/\.(mp4|webm|mov)$/i) ||
        resultUrl.includes('video')
      ) {
        return { mediaUrl: resultUrl, mediaType: 'video' as const };
      }
      if (resultUrl.match(/\.(mp3|wav|ogg)$/i) || resultUrl.includes('audio')) {
        return { mediaUrl: resultUrl, mediaType: 'audio' as const };
      }
    }
    return { mediaUrl: undefined, mediaType: undefined };
  }, [data.result]);

  const shouldShowResult = !!data.result;
  const renderResult = () => {
    if (!data.result) {
      return (
        <div className='text-center py-4'>
          <svg
            className='w-12 h-12 mx-auto text-gray-300 mb-2'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
            <path d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
          </svg>
          <p className='text-xs text-gray-500'>
            {t('Run workflow to see output')}
          </p>
        </div>
      );
    }

    // Handle different result types
    if (typeof data.result === 'string') {
      const resultUrl = data.result;
      // Check if it's an image URL
      if (
        resultUrl.match(/\.(jpeg|jpg|gif|png|webp)$/i) ||
        resultUrl.includes('image')
      ) {
        return (
          <div className='relative group'>
            <img
              src={resultUrl}
              alt={t('Output')}
              className='w-full rounded-md'
            />
            <div className='absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
              <button
                onClick={() => window.open(resultUrl, '_blank')}
                className='p-1.5 bg-white rounded-md shadow-lg hover:bg-gray-50 transition-colors'
                title={t('Open in new tab')}
              >
                <svg
                  className='w-4 h-4 text-gray-700'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                </svg>
              </button>
              <button
                onClick={() => downloadImage(resultUrl, 'output.png')}
                className='p-1.5 bg-white rounded-md shadow-lg hover:bg-gray-50 transition-colors'
                title={t('Download')}
              >
                <svg
                  className='w-4 h-4 text-gray-700'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' />
                </svg>
              </button>
            </div>
          </div>
        );
      }

      // Check if it's a video URL
      if (
        resultUrl.match(/\.(mp4|webm|mov)$/i) ||
        resultUrl.includes('video')
      ) {
        return (
          <div>
            <video
              src={resultUrl}
              controls
              className='w-full rounded-md'
              autoPlay={!isMobile}
              preload={isMobile ? 'metadata' : 'auto'}
              muted
              playsInline
              loop
            />
            <a
              href={resultUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='text-xs text-primary-600 hover:text-primary-700 mt-2 inline-block'
            >
              {t('Open in new tab')} →
            </a>
          </div>
        );
      }

      // Text output
      return (
        <div className='bg-gray-50 px-3 py-2 rounded-md'>
          <p className='text-sm text-gray-800 whitespace-pre-wrap break-words'>
            {data.result}
          </p>
        </div>
      );
    }

    // Handle array of results
    if (Array.isArray(data.result)) {
      return (
        <div className='space-y-2'>
          <p className='text-xs text-gray-600'>
            {data.result.length}{' '}
            {data.result.length === 1 ? t('result') : t('results')}
          </p>
          <div className='grid grid-cols-2 gap-2'>
            {data.result.slice(0, 4).map((item, i) => {
              if (
                typeof item === 'string' &&
                item.match(/\.(jpeg|jpg|gif|png|webp)$/i)
              ) {
                return (
                  <div key={i} className='relative group'>
                    <img
                      src={item}
                      alt={t('Result {{num}}', { num: i + 1 })}
                      className='w-full h-20 object-contain rounded-md bg-gray-100'
                    />
                    <div className='absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity'>
                      <button
                        onClick={() => window.open(item, '_blank')}
                        className='p-1 bg-white rounded shadow hover:bg-gray-50 transition-colors'
                        title={t('Open in new tab')}
                      >
                        <svg
                          className='w-3 h-3 text-gray-700'
                          fill='none'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                        </svg>
                      </button>
                      <button
                        onClick={() =>
                          downloadImage(item, `output-${i + 1}.png`)
                        }
                        className='p-1 bg-white rounded shadow hover:bg-gray-50 transition-colors'
                        title={t('Download')}
                      >
                        <svg
                          className='w-3 h-3 text-gray-700'
                          fill='none'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={i}
                  className='bg-gray-50 px-2 py-1 rounded text-xs text-gray-700 truncate'
                >
                  {String(item)}
                </div>
              );
            })}
          </div>
          {data.result.length > 4 && (
            <p className='text-xs text-gray-500 text-center'>
              +{data.result.length - 4} {t('more')}
            </p>
          )}
        </div>
      );
    }

    // Handle object output
    if (typeof data.result === 'object') {
      return (
        <div className='bg-gray-50 px-3 py-2 rounded-md max-h-40 overflow-auto'>
          <pre className='text-xs text-gray-800'>
            {JSON.stringify(data.result, null, 2)}
          </pre>
        </div>
      );
    }

    return <div className='text-xs text-gray-600'>{String(data.result)}</div>;
  };

  return (
    <>
      <BaseNode
        title={
          (typeof (data as { label?: unknown })?.label === 'string' &&
            (data as { label?: string }).label) ||
          t('Output')
        }
        icon={
          <svg
            className='w-4 h-4 text-green-600'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        }
        hasInput={true}
        hasOutput={false}
        status={((s) => {
          return typeof s === 'string' &&
            ['idle', 'running', 'success', 'error'].includes(s)
            ? (s as 'idle' | 'running' | 'success' | 'error')
            : undefined;
        })((data as { status?: unknown })?.status)}
        selected={selected}
        nodeId={nodeId || id}
        onRunNode={onRunNode}
        hideToolbarActions
        customHeaderButtons={
          <NodeActionsDropdown
            nodeId={nodeId || id}
            nodeTitle={(data.label as string) || t('Output')}
            mediaUrl={mediaUrl}
            mediaType={mediaType || 'image'}
            filename={`output.${mediaType === 'image' ? 'png' : mediaType === 'video' ? 'mp4' : 'mp3'}`}
            onEditImage={
              mediaUrl && mediaType === 'image'
                ? () => setShowEditModal(true)
                : undefined
            }
          />
        }
      >
        <div className='space-y-2'>
          <label className='block text-xs font-medium text-gray-700'>
            {t('Result')}
          </label>
          {renderResult()}
        </div>
      </BaseNode>
      {mediaUrl && mediaType === 'image' && (
        <EditModal
          image={{ image_url: mediaUrl }}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={(updatedImage) => {
            if (updatedImage.image_url) {
              updateNodeData(id, { result: updatedImage.image_url });
            }
          }}
        />
      )}
    </>
  );
}
